import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo } from 'react';
import { useApiClient } from '../../../hooks/useRestAPIHooks';
import { isNil } from 'lodash';

type TemplateId = string;
type TaskId = string;
type TemplateInvocationTask = {
  id: string;
  status:
    | 'scheduled'
    | 'pending'
    | 'processing'
    | 'failed'
    | 'completed'
    | 'expired';
  created: number;
  scheduled?: number | undefined;
  agentInput: unknown;
};

type TemplateTask = TemplateInvocationTask & {
  templateId: TemplateId;
};

const isTaskResolved = (task: TemplateInvocationTask) =>
  ['failed', 'completed', 'expired'].includes(task.status);

export const useAITemplateInvocations = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data: tasks } = useQuery<[TemplateId, TaskId][]>({
    queryKey: ['ai-templates', 'invocations', 'tasks'],
    queryFn: () => [],
    initialData: [],
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const taskQueries = useQueries({
    queries: tasks.map(([templateId, taskId]) => ({
      queryKey: ['ai-templates', 'invocation', templateId, taskId],
      queryFn: async () => {
        const response = await apiClient.request(
          'GET /v1/agents-v2/template-agent/invocations/:task_id',
          {
            task_id: taskId,
          },
        );
        return response.data
          ? ({ templateId, ...response.data } as TemplateTask)
          : null;
      },
      refetchInterval: (data: unknown) => {
        const task = data as TemplateTask | null;
        if (!task || isTaskResolved(task)) {
          return false;
        }
        return 5000;
      },
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });

  const invocations = useMemo(() => {
    const tasks = taskQueries
      .map(({ data }) => data)
      .filter(data => !isNil(data)) as TemplateTask[];

    const taskResults: Record<TemplateId, { isResolved: boolean }> = {};

    tasks.forEach(task => {
      taskResults[task.templateId] = {
        isResolved:
          taskResults[task.templateId]?.isResolved === false
            ? false
            : isTaskResolved(task),
      };
    });

    return taskResults;
  }, [taskQueries]);

  const addTask = useCallback(
    (task: [TemplateId, TaskId]) => {
      queryClient.setQueryData(
        ['ai-templates', 'invocations', 'tasks'],
        (old?: [TemplateId, TaskId][]) => [...(old ?? []), task],
      );
    },
    [queryClient],
  );

  return {
    addTask,
    invocations,
  };
};
